import type { WritableComputedRef } from 'vue'
import type { ExtraFieldMultiValue, ExtraFieldName, ExtraFieldValue, MultiValueExtraFieldName } from '@shared/model'

import { useTasksKanbanStore } from '../store'

type ExtraFieldModelValue = string | number | undefined
export type ExtraFieldModel = WritableComputedRef<ExtraFieldModelValue>
export type ExtraFieldMultiModel = WritableComputedRef<ExtraFieldMultiValue>

/** A field rendered as a single select can still hold a list (a task raised from
 *  CS with several reasons) — show the first entry rather than the raw array. */
const toSingleValue = (value: ExtraFieldValue): ExtraFieldModelValue => {
  if (Array.isArray(value)) return value[0]
  return value ?? undefined
}

/** Normalises both shapes the backend may hold: a list, or a single value left
 *  over from before the field became multi-select. */
const toMultiValue = (value: ExtraFieldValue): ExtraFieldMultiValue => {
  if (Array.isArray(value)) return value
  if (value === null || value === '') return []

  return [value]
}

export const useExtraFieldModel = (name: ExtraFieldName): ExtraFieldModel => {
  const tasksKanbanStore = useTasksKanbanStore()

  return computed<ExtraFieldModelValue>({
    get: () => toSingleValue(tasksKanbanStore.getExtraField(name)),
    set: (value) => tasksKanbanStore.setExtraField({ name, value: value ?? null }),
  })
}

export const useExtraFieldModelMulti = (name: MultiValueExtraFieldName): ExtraFieldMultiModel => {
  const tasksKanbanStore = useTasksKanbanStore()

  return computed<ExtraFieldMultiValue>({
    get: () => toMultiValue(tasksKanbanStore.getExtraField(name)),
    set: (value) => tasksKanbanStore.setExtraField({ name, value: [...value] }),
  })
}
